// 성적기록 엑셀 워크북 빌더 — 참고 구현(시니어 시안 검증본, R1 반영).
// 순수 함수: React·화면 상태를 모른다. 호출자가 model(JSON)만 넘긴다.
// 사용: const ExcelJS = (await import("exceljs")).default;
//       const wb = buildGradeSheetWorkbook(ExcelJS, model);
//       const buf = await wb.xlsx.writeBuffer();

const T = {
  font: "맑은 고딕",
  text: "FF1F2933",
  navy: "FF1F3A5F",
  headFill: "FFDDE5EF",
  labelFill: "FFF3F6FA",
  infoLabelFill: "FFE8EDF3",
  sumFill: "FFFFF6DB",
  rankFill: "FFE9F2FC",
  border: "FFB4BDC9",
  borderStrong: "FF5B6B80",
  excludeFill: "FFEDEDED",
  muted: "FF6B7785",
};
const thin = { style: "thin", color: { argb: T.border } };
const BOX = { top: thin, left: thin, bottom: thin, right: thin };

// --- 크기 규칙 (R1) -------------------------------------------------------
const LABEL_W = 15;          // 구분/평가항목(라벨) 열 너비
const STUDENT_W = 8.28;      // 학생 열 너비 (한글 4글자까지 한 줄)
const STUDENT_CAP = 8;       // 학생 열 한 줄 용량(반각 단위; 한글 1자 = 2)
const LABEL_CAP_PER_COL = 14; // 라벨 열 1칸의 한 줄 용량
const LINE_PT = 13.2;        // 맑은 고딕 10pt 한 줄 높이(pt)
const PAD_PT = 2;            // 위아래 여백 합계(pt) — 타이트하게
const rowH = (lines) => Math.round((Math.max(1, lines) * LINE_PT + PAD_PT) * 10) / 10;

// 글자 폭 단위: 한글·한자·전각 = 2, 그 외 = 1
const unitsOf = (str) =>
  [...String(str)].reduce((n, ch) => n + (ch.codePointAt(0) >= 0x1100 ? 2 : 1), 0);

// 칸 너비(cap) 안에서 자동 줄바꿈될 때의 줄 수 (\n 과 공백 단위 줄바꿈 모사)
function wrapLines(text, cap) {
  if (text === "" || text === null || text === undefined) return 1;
  let total = 0;
  String(text).split("\n").forEach((seg) => {
    let lines = 1;
    let cur = 0;
    seg.split(" ").forEach((tok) => {
      const w = unitsOf(tok);
      if (cur > 0 && cur + 1 + w <= cap) { cur += 1 + w; return; }
      if (cur > 0) { lines += 1; cur = 0; }
      if (w <= cap) { cur = w; return; }
      const extra = Math.ceil(w / cap) - 1;   // 칸보다 긴 한 덩어리는 글자 단위로 꺾임
      lines += extra;
      cur = w - extra * cap;
    });
    total += lines;
  });
  return total;
}

const SUMMARY_STYLE = {
  sum: { fill: T.sumFill, bold: true },
  avg: { fill: T.labelFill, numFmt: "0.0" },
  convert: { fill: T.labelFill },
  rank: { fill: T.rankFill },
  rankPercent: { fill: T.rankFill, numFmt: "0.0%" },
  rankClass: { fill: T.rankFill, bold: true, color: T.navy },
  exclude: { fill: null },            // 통계제외 행: 라벨만 labelFill, 칸은 "제외"일 때만 회색
};

function applyStyle(cell, o = {}) {
  cell.font = {
    name: T.font, size: 10, bold: !!o.bold, color: { argb: o.color || T.text },
  };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  if (o.fill) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: o.fill } };
  cell.border = BOX;
}

const hasChildren = (it) => Array.isArray(it.children) && it.children.length > 0;
const leavesOf = (it) => (hasChildren(it) ? it.children.flatMap(leavesOf) : [it]);
const depthOf = (items, level = 1) =>
  Math.max(level, ...items.map((it) => (hasChildren(it) ? depthOf(it.children, level + 1) : level)));

// 화면 문구(i18n)는 호출자가 model.labels 로 넘긴다. 없으면 한글 기본값.
const DEFAULT_LABELS = {
  weight: "가중치", total: "만점", division: "구분",
  levels: ["1단계", "2단계", "3단계"],
};

// 라벨: 이름 + (가중치) + (말단일 때 만점) — 화면 라벨 셀과 같은 내용
const makeLabelOf = (L) => (it) =>
  [
    it.content,
    it.weight ? `${L.weight}: ${it.weight}%` : null,
    it.total && !hasChildren(it) ? `${L.total}: ${it.total}` : null,
  ].filter(Boolean).join("\n");

export function buildGradeSheetWorkbook(ExcelJS, model) {
  const { info, students, items, summaryRows } = model;
  const L = { ...DEFAULT_LABELS, ...(model.labels || {}) };
  const labelOf = makeLabelOf(L);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("성적기록");

  const LC = depthOf(items);            // 라벨 열 수 (1~3)
  const SC = LC + 1;                    // 학생 시작 열
  const lastCol = LC + students.length;

  for (let c = 1; c <= LC; c++) ws.getColumn(c).width = LABEL_W;
  for (let c = SC; c <= lastCol; c++) ws.getColumn(c).width = STUDENT_W;

  // 1) 상단 과목 정보 (2행)
  let col = 1;
  info.forEach(({ label, value, span = 1 }) => {
    const c2 = col + span - 1;
    if (span > 1) { ws.mergeCells(1, col, 1, c2); ws.mergeCells(2, col, 2, c2); }
    for (let c = col; c <= c2; c++) {
      applyStyle(ws.getCell(1, c), { bold: true, fill: T.infoLabelFill });
      applyStyle(ws.getCell(2, c));
    }
    ws.getCell(1, col).value = label;
    ws.getCell(2, col).value = value ?? "";
    col = c2 + 1;
  });
  ws.getRow(1).height = 20; ws.getRow(2).height = 22; ws.getRow(3).height = 8;

  // 2) 헤더 2행: [구분 | 연번] / [단계 | 학생 이름]
  const H1 = 4, H2 = 5;
  if (LC > 1) ws.mergeCells(H1, 1, H1, LC);
  for (let c = 1; c <= LC; c++) applyStyle(ws.getCell(H1, c), { bold: true, fill: T.headFill });
  ws.getCell(H1, 1).value = L.division;
  L.levels.slice(0, LC).forEach((s, i) => {
    applyStyle(ws.getCell(H2, i + 1), { bold: true, fill: T.headFill });
    ws.getCell(H2, i + 1).value = s;
  });
  students.forEach((st, i) => {
    applyStyle(ws.getCell(H1, SC + i), { bold: true, fill: T.headFill });
    ws.getCell(H1, SC + i).value = i + 1;
    applyStyle(ws.getCell(H2, SC + i), { bold: true, fill: T.headFill });
    ws.getCell(H2, SC + i).value = st.name;
  });
  ws.getRow(H1).height = rowH(1);
  ws.getRow(H2).height = rowH(Math.max(1, ...students.map((st) => wrapLines(st.name, STUDENT_CAP))));

  // 3) 평가항목 행 (트리 → 말단 행, 병합)
  let r = H2 + 1;
  const putLabel = (row, c1, c2, it, rows = 1) => {
    const r2 = row + rows - 1;
    if (c2 > c1 || r2 > row) ws.mergeCells(row, c1, r2, c2);
    for (let rr = row; rr <= r2; rr++)
      for (let cc = c1; cc <= c2; cc++)
        applyStyle(ws.getCell(rr, cc), { fill: T.labelFill, bold: hasChildren(it) && rr === row && cc === c1 });
    ws.getCell(row, c1).value = labelOf(it);
  };
  const walk = (it, level, row) => {
    if (!hasChildren(it)) {
      putLabel(row, level, LC, it);
      const labelLines = wrapLines(labelOf(it), (LC - level + 1) * LABEL_CAP_PER_COL);
      const valueLines = Math.max(1, ...(it.values || []).map((v) => wrapLines(v, STUDENT_CAP)));
      ws.getRow(row).height = rowH(Math.max(labelLines, valueLines));
      students.forEach((_, i) => {
        const cell = ws.getCell(row, SC + i);
        applyStyle(cell);
        const v = it.values?.[i];
        cell.value = v === "" || v === undefined ? null : v;
      });
      return 1;
    }
    const n = leavesOf(it).length;
    putLabel(row, level, level, it, n);
    let used = 0;
    it.children.forEach((ch) => { used += walk(ch, level + 1, row + used); });
    return n;
  };
  items.forEach((it) => { r += walk(it, 1, r); });

  // 4) 요약 행 (화면에서 보이는 행만 model.summaryRows 에 담아 온다)
  const firstSummary = r;
  summaryRows.forEach(({ kind, label, values }) => {
    const s = SUMMARY_STYLE[kind] || {};
    if (LC > 1) ws.mergeCells(r, 1, r, LC);
    for (let c = 1; c <= LC; c++) applyStyle(ws.getCell(r, c), { bold: true, fill: s.fill || T.labelFill });
    ws.getCell(r, 1).value = label;
    students.forEach((_, i) => {
      const cell = ws.getCell(r, SC + i);
      const v = values?.[i];
      const hasValue = v !== "" && v !== null && v !== undefined;
      const marked = kind === "exclude" && hasValue;       // 통계제외 표시 칸만 회색
      applyStyle(cell, {
        bold: s.bold,
        fill: marked ? T.excludeFill : s.fill,
        color: marked ? T.muted : s.color,
      });
      if (hasValue) {
        cell.value = v;
        if (s.numFmt && typeof v === "number") cell.numFmt = s.numFmt;
      }
    });
    ws.getRow(r).height = rowH(Math.max(1, ...(values || []).map((v) => wrapLines(v, STUDENT_CAP))));
    r += 1;
  });
  if (summaryRows.length > 0) {      // 요약 구간 위 굵은 선
    for (let c = 1; c <= lastCol; c++) {
      const cell = ws.getCell(firstSummary, c);
      cell.border = { ...cell.border, top: { style: "medium", color: { argb: T.borderStrong } } };
    }
  }

  // 5) 틀 고정 + 눈금선 숨김
  ws.views = [{ state: "frozen", xSplit: LC, ySplit: H2, showGridLines: false }];
  return wb;
}
